import { useCallback, useEffect, useState } from 'react'
import offlineApi from '../utils/offlineApi'
import { API_ENDPOINTS } from '../config/api'
import { productsDB } from '../db/indexedDB'

/**
 * Shared hook for fetching cashier-facing branch stock products.
 * Keeps UI components lean and ensures a single source of truth.
 */
const useCashierProducts = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const fetchProducts = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      // Try to get from offline API (handles offline automatically)
      const response = await offlineApi.get(API_ENDPOINTS.BRANCH_STOCK)
      
      // Handle cached response
      if (response.fromCache) {
        console.log('Using cached products (offline mode)')
      }
      
      const data = response.data
      setProducts(Array.isArray(data) ? data : [])
      
      // If we got fresh data, update IndexedDB
      if (!response.fromCache && Array.isArray(data)) {
        await productsDB.bulkUpsert(data)
      }
    } catch (err) {
      // If network fails, try IndexedDB directly
      if (!navigator.onLine || err.message === 'Network Error') {
        try {
          const cachedProducts = await productsDB.getAll()
          if (cachedProducts.length > 0) {
            setProducts(cachedProducts)
            setError('Using offline data. Some products may be outdated.')
            return
          }
        } catch (dbError) {
          console.error('Error reading from IndexedDB:', dbError)
        }
      }
      
      const detail = err?.response?.data?.detail
      setError(detail || 'Unable to load products right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  return { products, loading, error, refresh: fetchProducts }
}

export default useCashierProducts


